import { useEffect } from 'react';
import { useLoaderData, useLocation, useNavigate } from 'react-router';
import { paths } from '../../../../../common/constants/pathVariables.js';
import { isDefined } from '../../../../../common/utils/objectUtils.js';
import { getCatalogWizardState, setCatalogWizardState } from '../../../../../components/CatalogConfiguration/utils.js';
import { mapLoaderDataToCatalogProducts } from '../../../../../components/CatalogProductsSelection/CatalogProductSelectionUtils.js';
import { CatalogProductsSelection } from '../../../../../components/CatalogProductsSelection/CatalogProductsSelection.js';
import { useConfig } from '../../../ConfigProvider';
import { useOnlineModelsContext } from '../../../onlineModel/onlineModelContextProvider.js';
import type { CatalogProductLoaderData, CatalogProducts } from '../../../../../common/loaders.js';
import type { CatalogProduct } from '../../../../../common/utils/catalogUtils.js';
import type { CatalogParams } from '../../../../../components/CatalogConfiguration/CatalogConfiguration.js';
import type { Catalog } from '../../../../../generated/api/catalog';
import type { OnlineModelCategory } from '../../../../../generated/api/onlineModelCategory';

export const CompanyInfoCatalogListProductsPath = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();
  const {
    allOnlineModelCodes,
    onlineModelCodes,
    onlineModels: publicOnlineModels,
  } = useLoaderData() as CatalogProductLoaderData;
  const wizardState = location.state ?? getCatalogWizardState<CatalogParams & { mdmId?: string }>();

  // Priced headers hydrate in the background. Public loader data is the fallback so the page can render
  // immediately even when the private headers endpoint omits some codes for the current account.
  const { priceAlteredModels, fetchHeadersByCodes, isHeaderCodeResolved } = useOnlineModelsContext();
  const allCodesKey = allOnlineModelCodes.join(',');

  useEffect(() => {
    if (allOnlineModelCodes.length > 0) {
      fetchHeadersByCodes(allOnlineModelCodes);
    }
    // `allOnlineModelCodes` is represented by `allCodesKey` to keep the dependency stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCodesKey, fetchHeadersByCodes]);

  useEffect(() => {
    if (!wizardState) {
      navigate(paths.COMPANY_INFO_CATALOGS, { replace: true });
    }
  }, [wizardState, navigate]);

  if (!wizardState) {
    return null;
  }

  const pricedHeadersByCode = priceAlteredModels.onlineModelHeadersByCode ?? {};
  const publicHeadersByCode = Object.fromEntries(
    Object.values(publicOnlineModels)
      .flat()
      .map(header => [header.onlineModelCode, header])
  );
  const toHeaders = (codes: string[]) =>
    codes.map(code => pricedHeadersByCode[code] ?? publicHeadersByCode[code]).filter(isDefined);
  const onlineModels: CatalogProducts = {
    phones: toHeaders(onlineModelCodes.phones),
    accessories: toHeaders(onlineModelCodes.accessories),
    tablets: toHeaders(onlineModelCodes.tablets),
    computers: toHeaders(onlineModelCodes.computers),
    networkEquipment: toHeaders(onlineModelCodes.networkEquipment),
  };

  const currentCatalog: Catalog = wizardState.catalog;

  const navigateToSummary = (
    catalogProducts: Record<string, CatalogProduct[]>,
    removedDiscontinuedDevices: string[]
  ) => {
    const remainingDiscontinuedProducts = (currentCatalog.discontinuedProducts ?? []).filter(
      device => !removedDiscontinuedDevices.includes(device.onlineModelCode)
    );

    const products = Object.values(catalogProducts)
      .flatMap(e => e.map(f => f.code))
      .concat(remainingDiscontinuedProducts.map(dp => dp.onlineModelCode));
    const productCategories = Object.keys(catalogProducts) as OnlineModelCategory[];

    const nextState = {
      ...wizardState,
      catalog: {
        ...currentCatalog,
        products,
        productCategories,
      },
      catalogProducts,
      removedDiscontinuedDevices,
    };

    setCatalogWizardState(nextState);
    navigate(`${paths.COMPANY_INFO_CATALOG_LIST_INFO}?mdmId=${wizardState.mdmId}`, {
      state: nextState,
    });
  };

  return (
    <CatalogProductsSelection
      catalogProducts={mapLoaderDataToCatalogProducts(
        onlineModels,
        currentCatalog,
        code => !isHeaderCodeResolved(code)
      )}
      onSetProductsForCatalog={navigateToSummary}
      preSelectedProducts={wizardState.catalogProducts}
      contractPeriod={wizardState.catalog.contractPeriod}
      imagesBaseUrl={config.imagesBaseUrl}
      catalog={wizardState.catalog}
      preRemovedDiscontinuedDevices={wizardState.removedDiscontinuedDevices ?? []}
    />
  );
};
